import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useReducer } from 'react';
import MuiTextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import type { SavingAccountArgs } from '@this/src/domain/saving_accounts/saving_account';
import SavingAccount from '@this/src/domain/saving_accounts/saving_account';

interface Props {
  value: number | null;
}

interface State {
  savingAccounts: SavingAccount[];
  loading: boolean;
}

interface Response {
  saving_accounts: SavingAccountArgs[];
}

type Action = { type: 'SET_RESPONSE'; payload: Response } | { type: 'SET_LOADING'; payload: boolean };

const initialState: State = {
  savingAccounts: [],
  loading: false
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_RESPONSE':
      return {
        ...state,
        savingAccounts: action.payload.saving_accounts.map(args => new SavingAccount(args)),
        loading: false
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

const inputProps = {
  style: {
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: '3px',
    boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.06)',
    boxSizing: 'border-box' as const,
    fontFamily: "'Helvetica Neue', 'Helvetica', 'Roboto', 'Arial', sans-serif",
    fontSize: '1em',
    marginBottom: '0.75em',
    padding: '0.5em',
    transition: 'border-color',
    width: '100%'
  }
};

export const SelectSavingAccounts: React.FC<Props> = ({ value }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { savingAccounts, loading } = state;

  const fetchSavingAccounts = useCallback(() => {
    Fetcher.get<Response>('/arrangement/saving_accounts')
      .then(payload => dispatch({ type: 'SET_RESPONSE', payload }))
      .catch(error => {
        console.error(error);
      });
  }, [dispatch]);

  useEffect(() => {
    fetchSavingAccounts();
  }, [fetchSavingAccounts]);

  return loading ? (
    <MuiTextField variant="outlined" size="small" disabled InputProps={inputProps} />
  ) : (
    <Autocomplete
      options={savingAccounts}
      getOptionLabel={option =>
        `(ID: ${option.id}) ${option.bankName} ${option.branchName} ${option.accountTypeStr} ${option.number} ${option.holderName}`
      }
      value={savingAccounts.find(account => account.id === value) || null}
      renderInput={params => (
        <MuiTextField
          {...params}
          variant="outlined"
          size="small"
          disabled
          InputProps={{
            ...params.InputProps,
            ...inputProps
          }}
        />
      )}
      disabled
    />
  );
};
